import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";
import { ArrowRight } from "lucide-react";
import { getImageUrl } from "@/utils/image-url";

const PROGRAM_IMAGES = [
  "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1562774053-701939374585?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1519452635265-7b1fbfd1e4e0?w=600&h=400&fit=crop",
];

export default function UniversityPrograms({ content, onContentChange, isSelected }: SectionContentProps) {
  const safeContent = content || {};
  const programs = safeContent.programs || [
    { title: "Graduation Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Summer Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Undergraduate", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
    { title: "Online Program", description: "Embark on a journey of knowledge discovery, and growth at our university.", link: "#" },
  ];

  const addProgram = () => {
    const newPrograms = [...programs, { title: "New Program", description: "Description", link: "#" }];
    onContentChange?.({ ...safeContent, programs: newPrograms });
  };

  const removeProgram = (idx: number) => {
    const newPrograms = programs.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...safeContent, programs: newPrograms });
  };

  const updateProgram = (idx: number, field: string, value: string) => {
    const newPrograms = [...programs];
    newPrograms[idx] = { ...newPrograms[idx], [field]: value };
    onContentChange?.({ ...safeContent, programs: newPrograms });
  };

  return (
    <section className="bg-white py-20 px-4">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-10 flex items-end justify-between border-b border-gray-200 pb-6">
          <h2 className="text-3xl font-bold text-[#1A1A1A]">
            <EditableText
              value={safeContent.title || "Our Programs"}
              onChange={(val) => onContentChange?.({ ...safeContent, title: val })}
              as="span"
              placeholder="Section Title"
            />
          </h2>
          <a
            href={safeContent.viewAllLink || "#"}
            className="group flex items-center gap-2 text-sm font-medium text-[#FF6D1C] hover:text-[#e55a0f] transition-colors"
          >
            <EditableText
              value={safeContent.viewAllText || "more program"}
              onChange={(val) => onContentChange?.({ ...safeContent, viewAllText: val })}
              as="span"
              placeholder="View All Link"
            />
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        {/* Program Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {programs.map((program: any, idx: number) => {
            const rawBg = program.imageUrl || PROGRAM_IMAGES[idx % PROGRAM_IMAGES.length];
            const bgUrl = getImageUrl(rawBg) || rawBg;
            return (
              <div
                key={idx}
                className="group relative min-h-[320px] overflow-hidden rounded-xl bg-cover bg-center transition-all duration-300 hover:shadow-xl"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%), url('${bgUrl}')`,
                }}
              >
              {isSelected && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeProgram(idx); }}
                  className="absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                >
                  <PiTrash className="h-3 w-3" />
                </button>
              )}

              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h5 className="mb-2 text-xl font-bold">
                  <EditableText
                    value={program.title}
                    onChange={(val) => updateProgram(idx, "title", val)}
                    as="span"
                    placeholder="Program Title"
                  />
                </h5>
                <p className="mb-4 text-sm text-gray-200 opacity-0 transition-all duration-300 group-hover:opacity-100">
                  <EditableText
                    value={program.description}
                    onChange={(val) => updateProgram(idx, "description", val)}
                    as="span"
                    placeholder="Description"
                  />
                </p>
                <a
                  href={program.link || "#"}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-[#FF6D1C] transition-colors"
                >
                  Learn More
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          );
        })}

          {isSelected && (
            <button
              onClick={addProgram}
              className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-[#FF6D1C] hover:bg-orange-50 hover:text-[#FF6D1C] transition-all"
            >
              <PiPlus className="h-10 w-10 mb-2" />
              <span className="text-sm font-medium">Add Program</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
